import {
  getAboutPageSetting as fetchAboutPageSetting,
  updateCareerSectionOrder as updateCareerSectionOrderApi,
} from '../../api/aboutPageSettingApi';
import { AboutPageSettingData } from '../../dto/EntityRepository';
import { UpdateCareerSectionOrderReq } from '../../dto/ReqDtoRepository';
import { AboutPageSettingResponse } from '../../dto/ResDtoRepository';
import { usePageOwnerIdentityStore } from '../../store/pageOwnerIdentityStore';
import { useAboutPageSettingStore } from '../../store/aboutPageSettingStore';
import { useTempAlertStore } from '../../store/portal/tempAlertStore';
import { runCurrentArchiveRequest } from '../../util/currentArchiveRequest';
import { normalizeCareerSectionOrder } from '../../util/careerSectionOrder';
import type { ResourceScope, ResourceStatus } from '../../util/scopedResource';
import { useApiWrapper } from './apiWrapper';

interface UseAboutPageSettingStateResult {
  aboutPageSetting: AboutPageSettingData | null;
  settingOwnerAui: string | null;
  settingScope: ResourceScope | null;
  status: ResourceStatus;
}

interface UseAboutPageSettingActionsResult {
  getAboutPageSetting: () => Promise<void>;
  getAboutPageSettingOwner: () => Promise<void>;
  updateCareerSectionOrder: (data: UpdateCareerSectionOrderReq) => Promise<boolean>;
}

export const useAboutPageSettingState = (): UseAboutPageSettingStateResult => {
  const aboutPageSetting = useAboutPageSettingStore((state) => state.aboutPageSetting);
  const settingOwnerAui = useAboutPageSettingStore((state) => state.settingOwnerAui);
  const settingScope = useAboutPageSettingStore((state) => state.settingScope);
  const status = useAboutPageSettingStore((state) => state.status);

  return { aboutPageSetting, settingOwnerAui, settingScope, status };
};

export const useAboutPageSettingActions = (): UseAboutPageSettingActionsResult => {
  const pageOwnerAui = usePageOwnerIdentityStore((state) => state.pageOwner?.aui);
  const beginAboutPageSettingFetch = useAboutPageSettingStore(
    (state) => state.beginAboutPageSettingFetch,
  );
  const setAboutPageSetting = useAboutPageSettingStore(
    (state) => state.setAboutPageSetting,
  );
  const setAboutPageSettingError = useAboutPageSettingStore(
    (state) => state.setAboutPageSettingError,
  );
  const setUpdatedTempAlert = useTempAlertStore((state) => state.setUpdatedTempAlert);
  const withApiHandler = useApiWrapper();

  const normalizeResponse = (response: AboutPageSettingResponse): AboutPageSettingData => ({
    ...response.data,
    careerSectionOrder: normalizeCareerSectionOrder(response.data.careerSectionOrder),
  });

  const fetchSetting = async (aui: string, scope: ResourceScope) => {
    beginAboutPageSettingFetch(aui, scope);
    try {
      return await runCurrentArchiveRequest(
        aui,
        () => fetchAboutPageSetting(aui),
        (response) => setAboutPageSetting(normalizeResponse(response), aui, scope),
      );
    } catch (error) {
      setAboutPageSettingError();
      throw error;
    }
  };

  const handleFetchRequest = async (scope: ResourceScope): Promise<void> => {
    if (!pageOwnerAui) return;
    const aui = pageOwnerAui;
    await withApiHandler(
      () => fetchSetting(aui, scope),
      [aui, scope],
    );
  };

  const handleUpdateCareerSectionOrder = async (
    data: UpdateCareerSectionOrderReq,
  ): Promise<boolean> => {
    if (!pageOwnerAui) return false;
    const aui = pageOwnerAui;
    const normalizedRequest: UpdateCareerSectionOrderReq = {
      careerSectionOrder: normalizeCareerSectionOrder(data.careerSectionOrder),
    };
    const result = await withApiHandler(
      () => runCurrentArchiveRequest(
        aui,
        () => updateCareerSectionOrderApi(aui, normalizedRequest),
        (response) => {
          setAboutPageSetting(normalizeResponse(response), aui, 'owner');
          setUpdatedTempAlert();
          return true;
        },
      ),
      [aui, normalizedRequest],
    );

    return result === true;
  };

  return {
    getAboutPageSetting: () => handleFetchRequest('public'),
    getAboutPageSettingOwner: () => handleFetchRequest('owner'),
    updateCareerSectionOrder: handleUpdateCareerSectionOrder,
  };
};

export const useAboutPageSetting = () => ({
  ...useAboutPageSettingState(),
  ...useAboutPageSettingActions(),
});
